import * as S from "effect/Schema";
/**
 * The outbox message catalogue: one entry per business message type, holding
 * the type string and its payload schema. Producers encode with
 * `entry.payload` and enqueue `entry.type`; the event listener decodes with
 * `entry.message`. Both sides import the entry, so a payload change is one
 * edit that every producer and the consumer see at typecheck.
 */
export const defineMessage = (definition) => ({
    type: definition.type,
    payload: definition.payload,
    message: S.Struct({
        type: S.Literal(definition.type),
        payload: definition.payload,
    }),
});
/**
 * One row = one deleted user whose id must be stripped from every
 * `saved_searches` row's `filter` and `user_ids`. The listener's
 * runUserCleanup activity delegates the DB work to apps/api's
 * `/internal/cleanup-deleted-user`.
 */
export const UserDeleted = defineMessage({
    type: "user.deleted",
    payload: S.Struct({ userId: S.UUID }),
});
export const DearestStatusChanged = defineMessage({
    type: "dearest.status-changed",
    payload: S.Struct({ userId: S.UUID }),
});
/**
 * One row = one user whose Dearest app access must be revoked. The listener's
 * runDearestRemoveAccess activity delegates the Dearest API call to apps/api's
 * `/internal/dearest-remove-access`, so the Dearest API key never leaves
 * apps/api.
 */
export const DearestRemove = defineMessage({
    type: "dearest.remove",
    payload: S.Struct({
        userId: S.UUID,
        /**
         * Set when the user was deleted and their person outlived the delete
         * because a Dearest account still holds it: the person, and the customer
         * the deleted account belonged to. The delete takes both off the PimaOne
         * side, so Dearest finds the person's domain self-signup at that customer
         * through these alone.
         */
        personId: S.optional(S.UUID),
        clientId: S.optional(S.UUID),
    }),
});
/**
 * One row = one `dearest_shares` entry whose Dearest access must be granted.
 * The listener's runDearestGrantAccess activity writes the access record
 * into public.has_access in the transaction that rechecks the share.
 */
export const DearestShare = defineMessage({
    type: "dearest.share",
    payload: S.Struct({ dearestShareId: S.UUID }),
});
export const EmployeeCsvRow = S.Struct({
    client_id: S.Unknown,
    role: S.String,
    email: S.optional(S.String),
    first_name: S.optional(S.String),
    last_name: S.optional(S.String),
    department: S.optional(S.String),
    job_role: S.optional(S.String),
    location: S.optional(S.String),
    location_id: S.optional(S.String),
    personal_number: S.optional(S.String),
    personal_email: S.optional(S.String),
    date_of_birth: S.optional(S.String),
    additional_roles: S.optional(S.String),
    additional_job_roles: S.optional(S.String),
    language: S.optional(S.String),
    bulk_upload: S.optional(S.Boolean),
    _upload_index: S.optional(S.Number),
    email_notifications: S.optional(S.Boolean),
    public_registration: S.optional(S.Boolean),
    id: S.optional(S.Unknown),
    user_id: S.optional(S.Unknown),
    status: S.optional(S.Literal("draft")),
    operation_mode: S.optional(S.Union(S.Literal("create_only"), S.Literal("upsert_by_email"))),
    _upload_row_number: S.optional(S.Number),
    metadata: S.optional(S.Unknown),
});
export const UserUploaded = defineMessage({
    type: "user.uploaded",
    payload: S.Struct({ uploadId: S.UUID }),
});
export const UserUploadRecoveryRequested = defineMessage({
    type: "user.upload-recovery-requested",
    payload: S.Struct({}),
});
/**
 * Risks that became `assigned` in one operation. The listener tells the OHS
 * Officers of each risk's client and, for a risk of one location, the
 * Location Managers that location is shared with. The risk-assign operation in
 * domain-data enqueues the row in the transaction that flips the status, so a
 * notification failure never fails or undoes the assignment. The listener
 * acts as `actorId`, the account that assigned them, and resolves recipients
 * itself.
 */
export const RiskAssigned = defineMessage({
    type: "risk.assigned",
    payload: S.Struct({
        risks: S.NonEmptyArray(S.Struct({
            id: S.UUID,
            clientId: S.UUID,
            locationId: S.NullOr(S.UUID),
            isCompanyWide: S.Boolean,
            language: S.String,
        })),
        actorId: S.UUID,
        /**
         * Names this assignment across retries of the listener's activity: a
         * person already told for it is never told again, however late the retry.
         */
        assignmentId: S.UUID,
    }),
});
/**
 * A risk group moved to accepted, rejected or mitigated. The listener tells
 * the risk's creator, acting as `actorId`, the account that changed it. The
 * status-change operation in domain-data enqueues one row per group in the
 * transaction that writes the status.
 */
export const RiskStatusChanged = defineMessage({
    type: "risk.status-changed",
    payload: S.Struct({
        riskId: S.UUID,
        status: S.Literal("accepted", "rejected", "mitigated"),
        actorId: S.UUID,
        /** Names this change across retries of the listener's activity. */
        changeId: S.UUID,
        /** The risk's title as the change left it. */
        title: S.String,
        /** The rejection reason the change recorded, if any. */
        rejectionReason: S.NullOr(S.String),
    }),
});
function messageCatalogue(...messages) {
    return messages;
}
export const catalogue = messageCatalogue(UserDeleted, DearestRemove, DearestStatusChanged, DearestShare, UserUploaded, UserUploadRecoveryRequested, RiskAssigned, RiskStatusChanged);
/** Durable risk text intent. Actor and versions are established by domain-data. */
export const RiskTextTranslationTarget = S.Struct({
    collection: S.Literal("risk_instances"),
    sourceId: S.UUID,
    sourceVersion: S.Int,
    clientId: S.UUID,
    actorId: S.UUID,
    targetLanguage: S.Literal("de-DE", "en-US"),
    targetClientRef: S.UUID,
    targetId: S.NullOr(S.UUID),
    targetVersion: S.NullOr(S.Int),
});
export const MeasureTextTranslationTarget = S.Struct({
    collection: S.Literal("measures"),
    itemId: S.NonEmptyString,
    actorId: S.UUID,
});
export const FileTranslationTarget = S.Struct({
    collection: S.Literal("yp_files"),
    itemId: S.NonEmptyString,
    /** The account whose write queued the job; the job runs as them. Absent on rows queued before it was carried. */
    actorId: S.optional(S.UUID),
});
export const LegacyAssetTranslationTarget = S.Struct({
    collection: S.Literal("yp_folders", "online_courses"),
    itemId: S.NonEmptyString,
});
export const AssetTranslationTarget = S.Union(RiskTextTranslationTarget, MeasureTextTranslationTarget, FileTranslationTarget, LegacyAssetTranslationTarget);
//# sourceMappingURL=messages.js.map